'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Newspaper, Megaphone, Tag } from 'lucide-react';
import Footer from '@/components/Footer';


type NewsItem = {
  id: string;
  title: string;
  date: string;
  category: string;
  summary: string;
  content: string;
};

export default function NoticiasPage() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [filter, setFilter] = useState<string>('Todos');

  useEffect(() => {
    fetch('/news.json')
      .then(res => res.json())
      .then(data => {
        // Ordena por data mais recente
        const sorted = data.sort((a: NewsItem, b: NewsItem) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setNews(sorted);
      })
      .catch(err => console.error('Erro ao carregar notícias:', err));
  }, []);

  const categories = ['Todos', ...Array.from(new Set(news.map(n => n.category)))];

  const filteredNews = filter === 'Todos' 
    ? news 
    : news.filter(n => n.category === filter);

  const getCategoryIcon = (category: string) => {
    switch(category.toLowerCase()) {
      case 'eventos': return <Calendar className="w-4 h-4" />;
      case 'avisos': return <Megaphone className="w-4 h-4" />;
      default: return <Newspaper className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch(category.toLowerCase()) {
      case 'saúde': return 'bg-rose-100 text-rose-700 border-rose-200';
      case 'prefeitura': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'eventos': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'avisos': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-emerald-200 selection:text-emerald-900 pb-20">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-700 to-teal-800 text-white shadow-lg sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:bg-white/10 px-3 py-2 rounded-xl transition-colors group">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-bold hidden sm:inline">Voltar para o início</span>
          </Link>
          <div className="flex items-center gap-3">
            <Newspaper className="w-6 h-6 text-emerald-200" />
            <h1 className="text-xl font-black uppercase tracking-tighter italic">Notícias e Eventos</h1>
          </div>
          <div className="w-10 sm:w-32"></div> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {/* Hero Section */}
        <section className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tighter uppercase italic">
            Fique por dentro
          </h1>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            Acompanhe as últimas notícias, avisos da prefeitura e eventos da comunidade de Catanduvas PR.
          </p>
        </section>

        {/* Filtros */}
        <section className="flex flex-wrap items-center justify-center gap-3">
          <div className="flex items-center gap-2 text-slate-400 mr-2">
            <Tag className="w-5 h-5" />
            <span className="text-sm font-bold uppercase tracking-widest">Filtrar:</span>
          </div>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                filter === cat 
                  ? 'bg-emerald-700 text-white shadow-md shadow-emerald-200' 
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:border-slate-300'
              }`}
            >
              {cat}
            </button>
          ))}
        </section>

        {/* Lista de Notícias */}
        <section className="space-y-6">
          {filteredNews.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-[2rem] border border-dashed border-slate-300">
              <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Newspaper className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">Nenhuma notícia encontrada</h3>
              <p className="text-slate-500 mt-2">Não há publicações para esta categoria no momento.</p>
            </div>
          ) : (
            filteredNews.map(item => (
              <article 
                key={item.id} 
                className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow group"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest border ${getCategoryColor(item.category)}`}>
                    {getCategoryIcon(item.category)}
                    {item.category}
                  </div>
                  <time className="text-sm font-bold text-slate-400 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(item.date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </time>
                </div>
                
                <h2 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight leading-tight mb-3 group-hover:text-emerald-700 transition-colors">
                  {item.title}
                </h2>
                
                <p className="text-lg text-slate-600 font-medium mb-4 leading-relaxed">
                  {item.summary}
                </p>
                
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-slate-500 leading-relaxed">
                    {item.content}
                  </p>
                </div>
              </article>
            ))
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
